import React, { useState, useEffect } from "react";
import { Form, Button, Spinner, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { api } from "../../services";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { X, ArrowRightLeft, Repeat } from "lucide-react";
import { resolveTenantApiError } from "../../utils/resolveTenantApiError";
import {
  branchPosConflictTooltipKey,
  omitOppositePosShopIdsFromBranchPayload,
} from "../../utils/posReadOnly";

const BranchMapping = ({
  app,
  branchId,
  storeId,
  onSyncComplete,
  branch,
  isPending,
  mutationsDisabled = false,
  mutationsDisabledTooltip = null,
}) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const branchMappingRbacTip =
    mutationsDisabledTooltip ||
    t("rbac.apiMsgs.permissionDeniedManageBranchMappings");

  const wrapBranchMappingRbacTooltip = (node, { block = false } = {}) => {
    if (!mappingControlsDisabled) return node;
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip dir={i18n.dir()}>{mappingControlsTooltip}</Tooltip>}
      >
        <span
          className={block ? "d-block w-100" : "d-inline-block"}
          style={{ cursor: "not-allowed" }}
          tabIndex={0}
          aria-label={mappingControlsTooltip}
        >
          <span style={{ pointerEvents: "none", display: block ? "block" : "contents" }}>
            {node}
          </span>
        </span>
      </OverlayTrigger>
    );
  };

  const translateBackendMessage = (msg) => {
    if (!msg) return "";
    const translated = t(msg);
    return translated !== msg ? translated : msg;
  };

  const [shops, setShops] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [branchData, setBranchData] = useState(branch || null);
  const [fetchingBranch, setFetchingBranch] = useState(false);
  const [operationType, setOperationType] = useState(isPending ? "sync" : "map"); // "map" or "sync"
  const [isSynced, setIsSynced] = useState(false);
  const [isMapped, setIsMapped] = useState(false);
  const [hoverSync, setHoverSync] = useState(false);
  const [hoverPending, setHoverPending] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  /** True after `/...-shops/...` (or shops API) returns — avoids treating pre-fetch state as "ready". */
  const [shopsFetchCompleted, setShopsFetchCompleted] = useState(false);

  // Get platform name from app
  let platform = app?.application?.toLowerCase() || "";

  platform = platform.match(/(keeta|chefz|foodics|tabsense)/)?.[0];

  const activeBranch = branchData || branch;
  const posConflictKey = branchPosConflictTooltipKey(
    activeBranch,
    app?.application
  );
  const posMappingBlocked = !!posConflictKey;
  const mappingControlsDisabled = mutationsDisabled || posMappingBlocked;
  const mappingControlsTooltip = posMappingBlocked
    ? t(posConflictKey)
    : branchMappingRbacTip;

  const isRefreshDisabled =
    loading || submitting || mappingControlsDisabled;

  const UNIFIED_SYNC_PLATFORMS = ["keeta", "chefz", "foodics"];

  // Fetch branch details if not provided
  useEffect(() => {
    const fetchBranchDetails = async () => {
      if (branch || !storeId || !branchId) return;

      setFetchingBranch(true);
      try {
        const token = localStorage.getItem("token");
        const response = await api.get(
          `/stores/${storeId}/branch/${branchId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setBranchData(response.data);
      } catch (err) {
        console.error("Error fetching branch details:", err);
        toast.error(t("Failed to fetch branch details"));
      } finally {
        setFetchingBranch(false);
      }
    };

    fetchBranchDetails();
  }, [storeId, branchId, branch, t]);

  useEffect(() => {
    if (storeId && platform) {
      setIsInitializing(true);
      setShopsFetchCompleted(false);
      fetchBranches();
      fetchShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, platform]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data?.branches || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranches([]);
    }
  };

  const getShopsListEndpoint = () => {
    if (platform === "keeta") {
      return `/keeta-shops/store/${storeId}/all`;
    }
    if (platform === "foodics") {
      return `/foodics-shops/store/${storeId}/all`;
    }
    if (platform === "chefz") {
      return `/chefz-shops/store/${storeId}/all`;
    }
    if (platform === "tabsense") {
      return `/tabsense-shops/store/${storeId}/all`;
    }
    return `/shops/stores/${storeId}/all-filtered?platform=${platform}`;
  };

  const loadShopsList = async (token) => {
    const response = await api.get(getShopsListEndpoint(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const list = response.data?.data?.shops || [];
    setShops(list);
    return list;
  };

  const fetchShops = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await loadShopsList(token);
    } catch (err) {
      console.error("Error fetching shops:", err);
      setError(t("Failed to fetch shops"));
      toast.error(t("Failed to fetch shops"));
    } finally {
      setLoading(false);
      setShopsFetchCompleted(true);
    }
  };

  const syncShopsFromPlatform = async (token) => {
    const integrationId = app?._id;
    if (!storeId || !integrationId) {
      const msg = t("storeId and integrationId are required");
      setError(msg);
      toast.error(msg);
      return false;
    }

    const authHeaders = { Authorization: `Bearer ${token}` };

    if (platform === "tabsense") {
      const syncRes = await api.post(
        "/tabsense-shops/sync",
        { storeId, integrationId },
        { headers: authHeaders }
      );
      const syncMsg = syncRes.data?.message;
      if (syncMsg) {
        toast.success(translateBackendMessage(syncMsg) || syncMsg);
      }
      return true;
    }

    if (UNIFIED_SYNC_PLATFORMS.includes(platform)) {
      const syncRes = await api.post(
        "/shops/sync",
        { platform, storeId, integrationId },
        { headers: authHeaders }
      );
      const syncMsg = syncRes.data?.message;
      if (syncMsg) {
        toast.success(translateBackendMessage(syncMsg) || syncMsg);
      }
      return true;
    }

    return true;
  };

  const refreshShops = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");

      await fetchBranches();

      const synced = await syncShopsFromPlatform(token);
      if (!synced) return;

      await loadShopsList(token);
    } catch (err) {
      console.error("Error refreshing shops:", err);
      const rawMsg = err.response?.data?.message;
      const errorMsg = resolveTenantApiError(err, t, "Failed to fetch shops");
      setError(errorMsg);
      toast.error(
        (rawMsg && translateBackendMessage(String(rawMsg))) || errorMsg
      );
    } finally {
      setLoading(false);
      setShopsFetchCompleted(true);
    }
  };

  // Filter shops to exclude ones mapped to other branches (same logic as ViewBranchPage)
  useEffect(() => {
    if (
      !shopsFetchCompleted ||
      !branchId ||
      !platform ||
      !(branchData || branch) ||
      fetchingBranch
    ) {
      return;
    }
    filterShopsLinkedToOtherBranches();
    setIsInitializing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shops,
    branches,
    branchId,
    platform,
    branchData,
    branch,
    shopsFetchCompleted,
    fetchingBranch,
  ]);


  const findBranchById = (id) =>
    branches.find((b) => String(b._id) === String(id));

  const isPlatformSyncApp = (syncApp) => {
    const appName = syncApp.app?.toLowerCase() || "";
    return appName.includes(platform);
  };

  const getShopPlatformExternalId = (shop) => {
    if (platform === "keeta") return shop.keetaShopId;
    if (platform === "foodics") return shop.foodicsBranchId;
    if (platform === "chefz") return shop.chefzShopId;
    if (platform === "tabsense") return shop.tabsenseBranchId;
    return shop._id;
  };

  const findShopByPlatformExternalId = (externalId) =>
    shops.find(
      (s) => String(getShopPlatformExternalId(s)) === String(externalId)
    );

  const collectLinkedShopDocIds = (branchList) => {
    const linked = new Set();
    branchList.forEach((b) => {
      (b.syncedApps || []).forEach((syncApp) => {
        if (!isPlatformSyncApp(syncApp)) return;
        const externalId = syncApp.external_id || syncApp.appId;
        if (!externalId) return;
        const shop = findShopByPlatformExternalId(externalId);
        if (shop) linked.add(String(shop._id || shop.id));
      });
      if (platform === "chefz" && b.chefzShopId) {
        linked.add(String(b.chefzShopId));
      }
    });
    return linked;
  };

  const filterShopsLinkedToOtherBranches = () => {
    const currentBranch = findBranchById(branchId);
    const currentBranchShopIds = collectLinkedShopDocIds(
      currentBranch ? [currentBranch] : []
    );
    const allLinkedShopIds = collectLinkedShopDocIds(branches);

    const result = shops.filter((shop) => {
      const shopDocId = String(shop._id || shop.id);
      return (
        !allLinkedShopIds.has(shopDocId) ||
        currentBranchShopIds.has(shopDocId)
      );
    });

    setFilteredShops(result);

    const branchToCheck = branchData || branch;
    let hasMatchedShop = false;
    let syncedApp = null;

    if (branchToCheck?.syncedApps && platform) {
      syncedApp = branchToCheck.syncedApps.find((syncApp) =>
        isPlatformSyncApp(syncApp)
      );

      const mappedExternalId = syncedApp?.external_id || syncedApp?.appId;
      if (mappedExternalId) {
        const matchedShop = result.find(
          (shop) =>
            String(getShopPlatformExternalId(shop)) ===
            String(mappedExternalId)
        );

        if (matchedShop) {
          setSelectedShop(matchedShop._id || matchedShop.id);
          setIsMapped(true);
          hasMatchedShop = true;
        }
      }

      if (syncedApp?.synced_status === true) {
        setIsSynced(true);
      } else if (hasMatchedShop) {
        setOperationType("map");
      }
    }

    if (
      !hasMatchedShop &&
      platform === "chefz" &&
      branchToCheck?.chefzShopId
    ) {
      const matchedShop = result.find(
        (shop) => String(shop._id) === String(branchToCheck.chefzShopId)
      );

      if (matchedShop) {
        setSelectedShop(matchedShop._id || matchedShop.id);
        setIsMapped(true);
        setOperationType("map");
      }
    }
  };
  const handleChefzSync = async () => {
    if (mappingControlsDisabled) {
      toast.error(mappingControlsTooltip);
      return;
    }
    setSubmitting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const payload = {
        appId: app._id,
        branchId: branchId,
      };

      console.log("🔄 Sending Data to Register Branch to Chefz:", payload);

      const response = await api.post("/register-branch", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Branch Registered Successfully:", response.data);
      toast.success(t("Branch synced successfully with Chefz"));

      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (err) {
      console.error(
        "❌ Error Registering Branch:",
        err.response?.data || err.message
      );
      const errorMsg = resolveTenantApiError(
        err,
        t,
        "Error syncing branch with Chefz"
      );
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };
  const handleSubmit = async (e, shopId = null, allowEmpty = false) => {
    if (e) e.preventDefault();

    if (mappingControlsDisabled) {
      toast.error(mappingControlsTooltip);
      return;
    }

    // Handle sync operation for chefz
    if (operationType === "sync" && platform === "chefz") {
      return handleChefzSync();
    }

    // Handle map operation (existing logic)
    const shopToMap = shopId !== null ? shopId : selectedShop;

    if (posMappingBlocked && shopToMap) {
      toast.error(t(posConflictKey));
      return;
    }

    if (!shopToMap && !allowEmpty) {
      setError(t("Please select a shop"));
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const currentBranch = branchData || branch;

      if (!currentBranch) {
        throw new Error("Branch data is not available");
      }

      console.log("🔍 Branch object:", currentBranch);

      // Build payload with existing branch data (same as ViewBranchPage)
      const payload = {
        ...currentBranch,
        name: {
          en: currentBranch?.name || currentBranch?.name?.en || "",
          ...(currentBranch?.name_ar || currentBranch?.name?.ar
            ? { ar: currentBranch.name_ar || currentBranch?.name?.ar }
            : {}),
        },
        email: currentBranch?.email || "",
        address: currentBranch?.address || "",
        phone: currentBranch?.phone || "",
        city: currentBranch?.city || "",
        latitude:
          currentBranch?.latitude ||
          currentBranch?.location?.coordinates?.[1] ||
          0,
        longitude:
          currentBranch?.longitude ||
          currentBranch?.location?.coordinates?.[0] ||
          0,
      };

      // Add platform-specific shop ID (empty string to clear mapping)
      if (platform === "keeta") {
        payload.keetaShopId = shopToMap || "";
      } else if (platform === "foodics") {
        payload.foodicsBranchId = shopToMap || "";
        omitOppositePosShopIdsFromBranchPayload(payload, "foodics");
      } else if (platform === "chefz") {
        // Only include chefzShopId if the user is actively mapping a new shop
        // OR if chefz is already mapped to this branch (to preserve/update/clear it).
        // Omitting it entirely when neither is true avoids a backend warning when
        // the store has no Chefz AppIntegration.
        const hasChefzSync = (branchData || branch)?.syncedApps?.some(
          (sa) => sa.app?.toLowerCase().includes("chefz") && sa.external_id
        );
        if (shopToMap || hasChefzSync) {
          payload.chefzShopId = shopToMap || "";
        }
      } else if (platform === "tabsense") {
        const hasTabSenseSync = (branchData || branch)?.syncedApps?.some(
          (sa) => sa.app?.toLowerCase().includes("tabsense") && sa.external_id
        );
        if (shopToMap || hasTabSenseSync) {
          payload.tabsenseShopId = shopToMap || "";
        }
        omitOppositePosShopIdsFromBranchPayload(payload, "tabsense");
      }

      console.log("🔄 Sending payload:", payload);

      const putResponse = await api.put(`/stores/${storeId}/branch/${branchId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const apiWarnings = putResponse.data?.warnings;
      if (Array.isArray(apiWarnings) && apiWarnings.length > 0) {
        apiWarnings.forEach((w) => {
          const m = w?.message || "";
          const tr = translateBackendMessage(m);
          toast.warning(tr || m);
        });
      }

      toast.success(
        shopToMap
          ? t("Mapping completed successfully")
          : t("Mapping cleared successfully")
      );

      // Refetch branch details to update local state
      try {
        const branchResponse = await api.get(
          `/stores/${storeId}/branch/${branchId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setBranchData(branchResponse.data);
      } catch (fetchErr) {
        console.error("Error refetching branch details:", fetchErr);
      }

      await fetchBranches();

      if (onSyncComplete) {
        onSyncComplete();
      }

      // Keep selected shop if mapping was successful, only clear on unmapping
      if (!shopToMap) {
        setSelectedShop("");
        setIsMapped(false);
      } else if (!apiWarnings?.length) {
        setIsMapped(true);
      }
      setError("");
    } catch (err) {
      console.error("Error mapping shop:", err);
      const errorMsg = resolveTenantApiError(err, t, "Failed to map shop");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleShopChange = (e) => {
    const shopId = e.target.value;

    // When "Select a shop" option is chosen (empty value), clear the mapping like cross icon
    if (!shopId) {
      setSelectedShop("");
      handleSubmit(null, "", true);
    } else {
      // When actual shop is selected, map it
      setSelectedShop(shopId);
      handleSubmit(null, shopId);
    }
  };

  const getShopDisplayName = (shop) => {
    // Display format matching ViewBranchPage.js
    if (platform === "keeta") {
      return `${shop.keetaShopId} - ${shop.shopName}`;
    } else if (platform === "chefz") {
      return `${shop.chefzShopId} - ${shop.name}`;
    } else if (platform === "foodics") {
      return `${shop.foodicsBranchId} - ${shop.fullName || shop.branchName}`;
    } else if (platform === "tabsense") {
      return `${shop.tabsenseBranchId} - ${shop.name || shop.shopName || ""}`;
    }

    // Fallback for other platforms
    if (isArabic) {
      return shop.name_ar || shop.name || shop.displayName || t("Unnamed");
    }
    return shop.name || shop.displayName || t("Unnamed");
  };

  if (!platform) return null;

  const CustomRadio = ({ checked, onChange, disabled, style, label }) => (
    <div
      onClick={() => !disabled && onChange()}
      className={`d-flex align-items-center ${disabled ? "opacity-50" : ""}`}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      <div
        style={{
          width: "15px",
          height: "15px",
          borderRadius: "50%",
          border: "2px solid #030F1B",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginInlineEnd: label ? "10px" : "0",
          backgroundColor: "white",
          flexShrink: 0,
        }}
      >
        {checked && (
          <div
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              backgroundColor: "#030F1B",
            }}
          />
        )}
      </div>
      {label && <span style={{fontSize: '14px'}}>{label}</span>}
    </div>
  );

  return (
    <div className="branch-mapping-form">
      {posMappingBlocked && (
        <Alert variant="warning" className="mb-3">
          {t(posConflictKey)}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {(fetchingBranch || isInitializing) ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
          <div className="mt-2 text-muted">
            {fetchingBranch ? t("Loading branch details...") : t("Loading data...")}
          </div>
        </div>
      ) : (
        <div
          style={
            platform === "chefz"
              ? {
                  backgroundColor: "#F9F9F9",
                  padding: "12px",
                  borderRadius: "4px",
                }
              : {}
          }
        >
          <Form onSubmit={handleSubmit}>
            {/* Show shop selector only for map operation or non-chefz platforms */}
            {(platform === "chefz" ||
              operationType === "map" ||
              platform !== "chefz") && (
              <Form.Group
                className="mb-3 d-flex"
                style={{ alignItems: "center", position: "relative" }}
                onMouseEnter={() => setHoverPending(true)}
                onMouseLeave={() => setHoverPending(false)}
              >
                {platform === "chefz" &&
                  wrapBranchMappingRbacTooltip(
                    <CustomRadio
                      checked={operationType === "map"}
                      onChange={() => setOperationType("map")}
                      disabled={submitting || isPending || mappingControlsDisabled}
                      style={{ marginInlineEnd: "8px", marginBottom: 0 }}
                    />
                  )}
                <Form.Label
                  style={{
                    marginInlineEnd:
                      platform === "keeta"
                        ? "24px"
                        : platform === "foodics"
                        ? "34px"
                        : platform === "tabsense"
                        ? "2px"
                        : "0px",
                    display: "flex",
                    justifyItems: "center",
                    alignItems: "center",
                    color: "#030F1B",
                    fontWeight: 600,
                    fontSize: "14px",
                    width:
                      platform === "tabsense"
                        ? "100px"
                        : "100px",
                    flexShrink: platform === "tabsense" ? 0 : undefined,
                    whiteSpace: platform === "chefz" ? "" : "nowrap",
                    marginBottom: 0,
                  }}
                >
                  {app?.application} {t("Store")}
                </Form.Label>
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  wrapBranchMappingRbacTooltip(
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    width: "100%",
                    ...(platform === "tabsense"
                      ? { marginInlineStart: "20px" }
                      : {}),
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                      <Form.Select
                        value={selectedShop}
                        onChange={handleShopChange}
                        disabled={
                          submitting ||
                          (platform === "chefz" && operationType === "sync") ||
                          isPending ||
                          mappingControlsDisabled
                        }
                        required
                        className="flex-fill"
                        style={{
                          paddingRight:
                            !isArabic && selectedShop ? "2rem" : "0.75rem",
                          paddingLeft:
                            isArabic && selectedShop ? "2rem" : undefined,
                          appearance: selectedShop ? "none" : undefined,
                          WebkitAppearance: selectedShop ? "none" : undefined,
                          MozAppearance: selectedShop ? "none" : undefined,
                          backgroundImage: selectedShop ? "none" : undefined,
                        }}
                      >
                        {filteredShops.length > 0 ? (
                          <>
                            <option value="">{t("Select a shop")}</option>
                            {filteredShops.map((shop) => (
                              <option
                                key={shop._id || shop.id}
                                value={shop._id || shop.id}
                              >
                                {getShopDisplayName(shop)}
                              </option>
                            ))}
                          </>
                        ) : (
                          <option value="">{t("No shop available")}</option>
                        )}
                      </Form.Select>

                      {selectedShop && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedShop("");
                            handleSubmit(null, "", true);
                          }}
                          disabled={submitting || mappingControlsDisabled}
                          style={{
                            position: "absolute",
                            right: !isArabic ? "4px" : undefined,
                            left: isArabic ? "4px" : undefined,
                            background: "transparent",
                            border: "none",
                            fontSize: "1rem",
                            cursor: submitting ? "not-allowed" : "pointer",
                            color: "#999",
                            zIndex: 1,
                            opacity: submitting ? 0.5 : 1,
                          }}
                          title="Clear selection"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>

                  <div
                    onClick={!isRefreshDisabled ? refreshShops : undefined}
                    disabled={isRefreshDisabled}
                    style={{
                      borderRadius: 4,
                      padding: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#083B4C",
                      opacity: isRefreshDisabled ? 0.5 : 1,
                      cursor: isRefreshDisabled ? "not-allowed" : "pointer",
                      minWidth: "40px",
                      minHeight: "40px",
                      flexShrink: 0,
                    }}
                    title={t("Refresh shops")}
                  >
                    {loading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ color: "white", width: "20px", height: "20px" }}
                      />
                    ) : (
                      <Repeat
                        size={20}
                        style={{ padding: "0px" }}
                        color="white"
                      />
                    )}
                  </div>
                </div>,
                { block: true }
                )
                )}
                {isPending && hoverPending && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                      pointerEvents: "none",
                    }}
                  >
                    {t("Branch is pending approval from the platform")}
                  </div>
                )}
              </Form.Group>
            )}

            {/* Radio button selection for Chefz only - inline with SYNC button */}
            {platform === "chefz" && (
              <>
                {wrapBranchMappingRbacTooltip(
                <div 
                  className="d-flex align-items-center justify-content-between mb-3"
                  style={{
                    position: "relative",
                  }}
                  onMouseEnter={() => !mappingControlsDisabled && setHoverSync(true)}
                  onMouseLeave={() => !mappingControlsDisabled && setHoverSync(false)}
                >
                  <CustomRadio
                    label={t("Sync this branch")}
                    checked={operationType === "sync"}
                    onChange={() => setOperationType("sync")}
                    disabled={submitting || isMapped || mappingControlsDisabled}
                    style={{
                      color: "#030F1B",
                      fontWeight: "600",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={
                      submitting ||
                      operationType === "map" ||
                      (platform === "chefz" && isMapped) ||
                      mappingControlsDisabled
                    }
                    title={
                      !mappingControlsDisabled && isMapped
                        ? t("Unmap the branch to enable this option")
                        : ""
                    }
                    style={{
                      backgroundColor: isMapped ? "#ccc" : "#27AE60",
                      borderRadius: 4,
                      color: "white",
                      fontWeight: "bold",
                      padding: "5px 12px",
                      fontSize: "14px",
                      border: "none",
                      cursor: isMapped ? "not-allowed" : "pointer",
                    }}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t("Syncing...")}
                      </>
                    ) : (
                      isPending ? t("SYNC AGAIN") : t("SYNC")
                    )}
                  </button>
                  {isMapped && hoverSync && !mappingControlsDisabled && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        color: "white",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        zIndex: 1000,
                        pointerEvents: "none",
                      }}
                    >
                      {t("Unmap the branch to enable this option")}
                    </div>
                  )}
                </div>,
                { block: true }
                )}
              </>
            )}
          </Form>
        </div>
      )}
    </div>
  );
};

export default BranchMapping;
